import { useCallback, useMemo, useState } from "react";

import { Button, Chip, Theme, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  GridColDef,
  GridFilterModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";

const useStyles = makeStyles({
  filterButton: {
    "& .MuiBadge-badge": { display: "none" },
  },
});

interface IDataTableProps {
  columns: GridColDef[];
}

export default function useDataTable({ columns }: IDataTableProps) {
  const [filter, setFilter] = useState<GridFilterModel>({ items: [] });
  const [filterButtonEl, setFilterButtonEl] =
    useState<HTMLButtonElement | null>(null);
  const filteredColumn = useMemo(() => {
    if (!filter.items.length || filter.items[0].value === undefined) {
      return null;
    }
    return columns.find((column) => column.field === filter.items[0].field);
  }, [filter, columns]);
  const label = useMemo(() => {
    if (!filteredColumn) return;

    let value = filter.items[0].value?.toString();
    if (filteredColumn?.type === "singleSelect") {
      let selectValue = filter.items[0].value;
      if (!Array.isArray(filter.items[0].value)) {
        selectValue = [filter.items[0].value];
      }

      value = "";
      selectValue.forEach((item: any, index: number) => {
        value += `${
          (filteredColumn as any)
            .valueOptions()
            .find((option: any) => option.value === item)?.label
        }`;
        if (index !== selectValue.length - 1) {
          value += ", ";
        }
      });
    }
    return `${filteredColumn?.headerName!.toUpperCase()}: ${value?.toUpperCase()}`;
  }, [filteredColumn, filter.items]);
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));

  const classes = useStyles();

  const CustomToolbar = useCallback(() => {
    return (
      <GridToolbarContainer
        style={{
          marginTop: xs ? "-48px" : "-56px",
          padding: 0,
          display: "inline-flex",
          position: "absolute",
        }}
      >
        <GridToolbarFilterButton
          componentsProps={{ button: { variant: "outlined" } }}
          ref={setFilterButtonEl}
          className={classes.filterButton}
        />
        {filteredColumn && (
          <>
            <Chip
              label={label}
              style={{
                marginLeft: 10,
              }}
            />
            <Button
              size="small"
              color="secondary"
              variant="text"
              style={{ textTransform: "capitalize" }}
              onClick={() => setFilter({ items: [] })}
            >
              Reset
            </Button>
          </>
        )}
      </GridToolbarContainer>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xs, filter]);

  return {
    filterButtonEl,
    CustomToolbar,
    isFiltered: !!filteredColumn,
    filter,
    setFilter,
  };
}
